// ============================================================
// FIXED COURSE VISUAL-PAGE TEMPLATE
// ============================================================
// Geometry, hierarchy, palette AND default copy are taken from the
// course-page reference screenshot. Every new Course Visual Page starts from
// this template, no matter which course the admin picked on the Create
// screen.
//
// Rules this module enforces:
//
//   1. The selected course NEVER contributes layout, CSS, markup or content.
//      It contributes ONLY an nid + path, handed to the dynamic components
//      as their data source. Change the course and the tree is
//      byte-identical; only sourceNid / sourcePath move.
//
//   2. No header, no footer, no breadcrumb. Those are the app shell
//      (Header.js / Footer.js around the route); reproducing them here would
//      double them on the rendered page.
//
//   3. Nothing is invented. Every string below is transcribed from the
//      reference screenshot. Where the screenshot was not legible enough to
//      transcribe (the payment small-print block), the node is omitted
//      rather than filled with made-up text.
//
// NOTE ON THE DEFAULT COPY: the reference is the Baseball Scout/GM course,
// so that is the copy every new Course page starts with. That is what "the
// same fixed template always loads" means here — the admin edits the copy
// per course. Nothing about the copy is read from the selected course.
//
// Positions, in reference order:
//
//   0   Template CSS      responsive + row-width rules (see TEMPLATE_CSS)
//   1   Hero              copy + CTAs | portrait      72/28
//   2   Paragraph         two prose paragraphs
//   3   Chat box          WIRED  next-start-date element
//   4   Mentors           WIRED  dynamic-mentors
//   5   What You'll Learn heading + bulleted list
//   6   Alumni            WIRED  dynamic-success-stories
//   7   Jobs & Salary     heading + 35/15/35/15 pay grid, 4 rows
//   8   CTA               WIRED  Apply / Enroll actions
//   9   Course body       long-form sub-sections + CTAs
//   10  Course Details    bordered box, 8 rows of copy 85 / icon 15
//   11  Payment           heading + 4 x 25% plan cards
//   12  Quick Form        WIRED  dynamic-quick-form, navy panel
//   13  Related           heading + 3 bands x 3 cards
//
// Mentors has its own heading + sub-line (the mentor-card carousel renders
// only cards). Alumni and Quick Form still get NO heading here: SuccessStories
// emits <h2>Success Stories</h2> and SidebarQuickForm emits its own header, so
// a heading there would double-title them.
//
// Bulleted lists and bold run-in labels use `html` nodes because the node
// registry has no rich-text element — a `text` node cannot bold part of a
// line. This is template-authored markup with inline styles, never course
// markup, so no stylesheet can reshape it. Everything else uses ordinary
// heading / text / image / button / card / row / column nodes so it stays
// individually editable, draggable and resizable.
//
// Nodes are plain object literals rather than PageEditor's makeNode() on
// purpose: makeNode and the T registry are module-private to PageEditor.js,
// and importing PageEditor here would pull Mentor/Body/BlockRenderer and
// PageEditor.css into the Create-page bundle. Because makeNode's
// defaultProps merge is skipped, every prop a renderer reads is written out
// in full below.
//
// The one exception is the shared What You'll Learn default body: it is a plain
// string constant (no PageEditor/registry dependency), imported so the template
// and the palette-drop registry default stay in lockstep from one source.
import { WYL_DEFAULT_BULLETS } from '../whatYouLearnDefaults';

const uid = () => Math.random().toString(36).slice(2, 10);

const N = (type, props, children) => {
  const n = { id: uid(), type, props: props || {} };
  if (children !== undefined) n.children = children;
  return n;
};

// ------------------------------------------------------------
// Palette — read from the reference.
// ------------------------------------------------------------
const NAVY     = '#003C7D';  // headings, dark bands, form panel
const BLUE     = '#2C99D2';  // buttons, rules, pay pills, icons
const RED       = '#B32116'; // Apply Now / Enroll Now
const TEXT     = '#4A4A4A';  // body copy
const MUTED    = '#6B7280';  // captions, meta
const WHITE    = '#FFFFFF';
const LINE     = '#E4E6EB';  // borders / rules
const CELL_BG  = '#EAF2F8';  // pay-grid role cells
const HEAD_BG  = '#F1F4F7';  // Course Details header bar

// Reference constrains content to the site-wide 1170px container.
const CONTENT_MAX = 1170;

// ------------------------------------------------------------
// Template CSS
// ------------------------------------------------------------
// Scoped to `.page-editor-root .pe-canvas`, the wrapper used by BOTH the
// editing canvas and the public read-only render. Because the <style> lives
// inside this page's own tree it exists in the DOM only while a page built
// from this template is on screen — no other visual page is affected.
//
// The responsive block is convention, not something the reference (a single
// desktop screenshot) could tell us. Rows stack below 900px based on their
// existing `wrap` prop; a row left at wrap:"nowrap" stays side-by-side.
// Three selectors are needed because browsers re-serialise the inline
// `flex-wrap` + `flex-direction` pair into the `flex-flow` shorthand and
// disagree on the form.
const TEMPLATE_CSS = [
  '<style>',
  '/* Course Visual Page template - template-authored CSS. */',
  '',
  '/* Rows must hold the full container width even while their columns are',
  '   empty. .row-inner is a flex ITEM with the default flex:0 1 auto, so it',
  '   shrink-to-fits; columns carry flex-basis:0, so an untouched row would',
  '   collapse to just its gap. No-op for rows that already have content. */',
  '.page-editor-root .pe-canvas .n-row > .row-inner { flex: 1 1 auto; width: 100%; }',
  '',
  '/* Template-authored list/paragraph markup inside html nodes. */',
  '.page-editor-root .pe-canvas .tpl-list { margin: 0; padding-left: 20px; }',
  '.page-editor-root .pe-canvas .tpl-list li { margin: 0 0 7px; line-height: 1.6; }',
  '.page-editor-root .pe-canvas .tpl-rt { margin: 0 0 10px; line-height: 1.65; }',
  '',
  '/* Hero H1 — gold left accent bar. The heading node has no border field, so',
  '   the bar + padding come from here. h1 is used only by the hero title, so',
  '   this selector is effectively hero-scoped. font-size/color are set inline',
  '   on the node (28px / #003C7D) and are not overridden here. */',
  '.page-editor-root .pe-canvas .l-heading h1 {',
  '  border-left: 15px solid #f3ac3a;',
  '  padding-left: 10px;',
  '}',
  '',
  '/* Hero featured image card is now built from native nodes (navy Card +',
  '   Image + two Text nodes), so it needs no bespoke CSS here. */',
  '',
  '/* Next Start Date block: its full design AND fixed 365x162 size live in',
  '   PageEditor.css (.l-nsb), the component stylesheet loaded on every visual',
  '   page, so it renders correctly on old and new pages alike — nothing to',
  '   scope here. */',
  '',
  '@media (max-width: 900px) {',
  '  .page-editor-root .pe-canvas .row-inner[style*="flex-flow: wrap"],',
  '  .page-editor-root .pe-canvas .row-inner[style*="flex-flow: row wrap"],',
  '  .page-editor-root .pe-canvas .row-inner[style*="flex-wrap: wrap"] {',
  '    flex-direction: column !important;',
  '    align-items: stretch !important;',
  '    gap: 18px !important;',
  '  }',
  '  .page-editor-root .pe-canvas .row-inner[style*="flex-flow: wrap"] > .n-column,',
  '  .page-editor-root .pe-canvas .row-inner[style*="flex-flow: row wrap"] > .n-column,',
  '  .page-editor-root .pe-canvas .row-inner[style*="flex-wrap: wrap"] > .n-column {',
  '    flex: 1 1 auto !important;',
  '    width: 100% !important;',
  '  }',
  '  .page-editor-root .pe-canvas .n-section { padding-left: 16px !important; padding-right: 16px !important; }',
  '  .page-editor-root .pe-canvas .n-card { width: auto !important; }',
  '  .page-editor-root .pe-canvas .l-image img { max-width: 100% !important; height: auto !important; }',
  '  .page-editor-root .pe-canvas .l-button a { display: block !important; width: 100% !important; }',
  '  .page-editor-root .pe-canvas .l-heading h1 { font-size: 26px !important; line-height: 1.25 !important; }',
  '  .page-editor-root .pe-canvas .l-heading h2 { font-size: 19px !important; line-height: 1.3 !important; }',
  '  .page-editor-root .pe-canvas .l-heading h3 { font-size: 16px !important; }',
  '}',
  '@media (max-width: 560px) {',
  '  .page-editor-root .pe-canvas .n-section { padding-left: 12px !important; padding-right: 12px !important; }',
  '  .page-editor-root .pe-canvas .l-text p { font-size: 14px !important; }',
  '}',
  '</style>',
].join('\n');

// ------------------------------------------------------------
// Inline SVG artwork
// ------------------------------------------------------------
// The screenshot's photographs and icons can't be extracted from a
// screenshot, so image slots ship as neutral data-URI placeholders on
// ordinary `image` nodes. They stay first-class editable nodes — an admin
// swaps one for the real asset with the Inspector's upload / paste-URL
// field — and the template needs no external asset host.
const svgDataUri = (svg) =>
  'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

const circleIcon = (glyph) => svgDataUri(
  '<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72">' +
    '<circle cx="36" cy="36" r="36" fill="' + BLUE + '"/>' +
    '<g fill="none" stroke="#ffffff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">' +
      glyph +
    '</g>' +
  '</svg>'
);

const ICON_ZOOM   = circleIcon('<rect x="20" y="26" width="22" height="20" rx="3"/><path d="M42 33l10-5v16l-10-5z"/>');
const ICON_ONLINE = circleIcon('<rect x="22" y="25" width="28" height="19" rx="2"/><path d="M18 49h36"/>');
const ICON_GLOBE  = circleIcon('<circle cx="36" cy="36" r="15"/><path d="M21 36h30M36 21c4 5 4 25 0 30M36 21c-4 5-4 25 0 30"/>');
const ICON_CREDIT = circleIcon('<path d="M36 20l16 8-16 8-16-8z"/><path d="M26 32v10c0 3 4.5 6 10 6s10-3 10-6V32"/>');
const ICON_EVENT  = circleIcon('<rect x="21" y="24" width="30" height="26" rx="3"/><path d="M21 33h30M29 20v7M43 20v7"/>');
const ICON_CERT   = circleIcon('<path d="M27 21h18v22l-9-6-9 6z"/><circle cx="36" cy="30" r="4"/>');
const ICON_SHIELD = circleIcon('<path d="M36 20l14 6v11c0 9-6 14-14 17-8-3-14-8-14-17V26z"/>');
const ICON_TAX    = circleIcon('<path d="M24 22h20l6 6v22H24z"/><path d="M30 34h14M30 41h14"/>');

const photoPlaceholder = (w, h, label) => svgDataUri(
  '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + h + '" viewBox="0 0 ' + w + ' ' + h + '">' +
    '<rect width="' + w + '" height="' + h + '" fill="#E8ECF1"/>' +
    '<g fill="#C2CBD6">' +
      '<circle cx="' + (w / 2) + '" cy="' + (h * 0.38) + '" r="' + (h * 0.14) + '"/>' +
      '<path d="M' + (w / 2 - h * 0.26) + ' ' + (h * 0.74) +
        ' a' + (h * 0.26) + ' ' + (h * 0.26) + ' 0 0 1 ' + (h * 0.52) + ' 0 z"/>' +
    '</g>' +
    '<text x="' + (w / 2) + '" y="' + (h - 12) + '" text-anchor="middle" ' +
      'font-family="Arial,Helvetica,sans-serif" font-size="12" fill="#8B96A5">' + label + '</text>' +
  '</svg>'
);

const HERO_PHOTO = photoPlaceholder(420, 330, 'Mentor photo');
const CARD_PHOTO = photoPlaceholder(420, 300, 'Card image');

// ------------------------------------------------------------
// Node helpers — each writes a complete prop set so no renderer ever falls
// through to an ambient default.
// ------------------------------------------------------------
const SEC = (props, kids) => N('section', {
  background: WHITE,
  backgroundImage: '',
  padding: { t: 30, r: 20, b: 30, l: 20 },
  maxWidth: CONTENT_MAX,
  minHeight: 0,
  textAlign: 'left',
  ...props,
}, kids);

const ROW = (props, kids) => N('row', {
  gap: 24,
  align: 'stretch',
  justify: 'flex-start',
  wrap: 'wrap',
  reverse: false,
  minHeight: 0,
  ...props,
}, kids);

const COL = (props, kids) => N('column', {
  weight: 1,
  padding: { t: 0, r: 0, b: 0, l: 0 },
  background: '',
  radius: 0,
  minHeight: 0,
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  ...props,
}, kids);

const CARD = (props, kids) => N('card', {
  background: WHITE,
  padding: { t: 0, r: 0, b: 0, l: 0 },
  radius: 6,
  border: true,
  borderColor: LINE,
  shadow: false,
  width: '',
  minHeight: 0,
  ...props,
}, kids);

const H = (text, props) => N('heading', {
  text,
  level: 'h2',
  align: 'left',
  color: NAVY,
  weight: '700',
  size: 20,
  lineHeight: 0,
  letterSpacing: 0,
  marginTop: 0,
  marginBottom: 14,
  ...props,
});

const TX = (text, props) => N('text', {
  text,
  align: 'left',
  size: 14,
  color: TEXT,
  weight: '',
  lineHeight: 1.65,
  letterSpacing: 0,
  marginTop: 0,
  marginBottom: 12,
  ...props,
});

const IMG = (props) => N('image', {
  src: '',
  alt: '',
  caption: '',
  width: '100%',
  height: '',
  radius: 4,
  align: 'left',
  href: '',
  target: '_self',
  ...props,
});

// Atomic CTA node builder (module level so module-level card helpers can use
// it too). Emits the design-system `dynamic-cta` node — one base atom,
// Apply/Enroll variants. Background, hover, label text and the SKU-driven
// href all come from ctaConfig.js at render time and are NOT stored on the
// node, so they cannot drift per-page and edits happen in one place. The
// only per-instance choices are variant, alignment and full-width. Seeded
// with the page's sourcePath so it resolves this course's SKU automatically.
const ctaNode = (variant, sourcePath, opts, productId) => N('dynamic-cta', {
  variant,
  sourcePath: sourcePath ? String(sourcePath) : '',
  productId: productId || '',
  align: (opts && opts.align) || 'left',
  fullWidth: !!(opts && opts.fullWidth),
});

const BTN = (text, props) => N('button', {
  text,
  href: '#',
  target: '_self',
  align: 'left',
  size: 'md',
  background: BLUE,
  color: WHITE,
  radius: 3,
  weight: '700',
  borderWidth: 0,
  borderColor: NAVY,
  fullWidth: false,
  ...props,
});

const SP = (height) => N('spacer', { height });

const HTML = (html) => N('html', { html });

const DYN = (type, seed) => N(type, seed || {});

// Template-authored rich text. `<ul>` list with optional bold run-in labels.
const LIST = (items) => HTML(
  '<ul class="tpl-list" style="font-size:14px;color:' + TEXT + '">' +
  items.map((it) => typeof it === 'string'
    ? '<li>' + it + '</li>'
    : '<li><strong style="color:' + NAVY + '">' + it[0] + '</strong> ' + it[1] + '</li>').join('') +
  '</ul>'
);

// A paragraph with a bold blue run-in label, used by Course Details.
const RUNIN = (label, body) => HTML(
  '<p class="tpl-rt" style="font-size:13.5px;color:' + TEXT + '">' +
  '<strong style="color:' + BLUE + '">' + label + '</strong> ' + body + '</p>'
);

// One Course Details row: copy left (85%), circular icon right (15%).
// wrap:'nowrap' keeps the pairing intact on phones.
const detailRow = (label, body, icon) => ROW(
  { gap: 16, align: 'flex-start', wrap: 'nowrap' },
  [
    COL({ weight: 85 }, [RUNIN(label, body)]),
    COL({ weight: 15, alignItems: 'flex-end' }, [
      IMG({ src: icon, alt: '', width: '56px', radius: 28, align: 'right' }),
    ]),
  ]
);

// One [role | pay] pair in the Jobs & Salary grid.
const payCells = (role, pay) => [
  COL({ weight: 35, background: CELL_BG, radius: 3, padding: { t: 9, r: 12, b: 9, l: 12 } }, [
    TX(role, { size: 13, color: NAVY, marginBottom: 0 }),
  ]),
  COL({ weight: 15, background: BLUE, radius: 3, padding: { t: 9, r: 10, b: 9, l: 10 } }, [
    TX(pay, { size: 13, color: WHITE, weight: '700', align: 'center', marginBottom: 0 }),
  ]),
];

const payRow = (r1, p1, r2, p2) =>
  ROW({ gap: 10, align: 'stretch' }, [...payCells(r1, p1), ...payCells(r2, p2)]);

// One payment-plan card.
const planCard = (label, price, sub, sourcePath, productId) => COL({ weight: 1 }, [
  CARD({ padding: { t: 16, r: 14, b: 16, l: 14 }, radius: 4 }, [
    TX(label, { size: 12, color: MUTED, align: 'center', weight: '700', marginBottom: 8 }),
    H(price, { level: 'h3', size: 24, weight: '800', color: NAVY, align: 'center', marginBottom: sub ? 4 : 12 }),
    ...(sub ? [TX(sub, { size: 11, color: MUTED, align: 'center', marginBottom: 12 })] : []),
    ctaNode('enroll', sourcePath, { align: 'center', fullWidth: true }, productId),
  ]),
]);

// One related-item card.
const relCard = (title, meta, desc) => COL({ weight: 1 }, [
  CARD({ padding: { t: 0, r: 0, b: 14, l: 0 } }, [
    IMG({ src: CARD_PHOTO, alt: title, width: '100%', radius: 0 }),
    SP(12),
    COL({ weight: 1, padding: { t: 0, r: 14, b: 0, l: 14 } }, [
      H(title, { level: 'h4', size: 15, weight: '700', color: NAVY, marginBottom: 4 }),
      TX(meta, { size: 12, color: BLUE, weight: '600', marginBottom: 8 }),
      TX(desc, { size: 12.5, color: TEXT, lineHeight: 1.55, marginBottom: 12 }),
      BTN('LEARN MORE', { size: 'sm', align: 'left' }),
    ]),
  ]),
]);

// Small uppercase band label used between the Related grids.
const bandLabel = (text) => TX(text, {
  size: 11, color: MUTED, weight: '700', letterSpacing: 1.1, marginBottom: 10,
});

// ============================================================
// The template
// ============================================================
export function makeCourseTemplate({ nid, sourcePath, productId } = {}) {
  const rootProps = {};
  if (nid) rootProps.sourceNid = nid;
  if (sourcePath) rootProps.sourcePath = String(sourcePath);
  if (productId) rootProps.productId = productId;

  const nidSeed  = { sourceNid: nid ? nid : '' };
  const pathSeed = { sourcePath: sourcePath ? String(sourcePath) : '' };
  // Alumni (dynamic-success-stories) binds to the course product_id.
  const alumniSeed = { sourceNid: nid ? nid : '', productId: productId || '' };

  // Atomic CTA. Emits the design-system `dynamic-cta` node — one base atom,
  // Apply/Enroll variants. Background, hover, label text and the SKU-driven
  // href all come from ctaConfig.js at render time and are NOT stored on the
  // node, so they can never drift per-page and edits happen in one place.
  // The only per-instance choices are variant, alignment and full-width. The
  // node is seeded with the page's sourcePath so it resolves this course's
  // SKU automatically — no manual entry on create or on palette drop.
  const CTA = (variant, opts) => ctaNode(variant, sourcePath, opts, productId);

  // -- 1. Hero -------------------------------------------------------
  const hero = SEC(
    { padding: { t: 26, r: 20, b: 22, l: 20 } },
    [
      ROW({ gap: 40, align: 'flex-start' }, [
        // Left: headline, subheading, lead paragraph, CTA atoms.
        // Exact CSS per the approved hero component:
        //   H1  28px / #003C7D / 15px gold left bar (added via TEMPLATE_CSS,
        //       since the heading node has no border field) / 10px left pad
        //   H2  #003C7D   ·   paragraph 18px
        COL({ weight: 72 }, [
          H('', {
            // Blank authored text + a binding => the canvas shows the course's
            // LIVE title (getpagedata.title). Type in the Inspector Text field
            // to override it per page. Layout/typography unchanged.
            bind: 'title',
            level: 'h1', size: 28, weight: '700', color: NAVY,
            lineHeight: 1.2, marginBottom: 16,
          }),
          H('Learn scouting and front-office skills in 8 weeks, mentored live by working MLB executives and scouts.', {
            level: 'h2', size: 21, weight: '700', color: NAVY,
            lineHeight: 1.35, marginBottom: 16,
          }),
          TX('',
            // Blank authored text + a binding => the canvas shows the course's
            // LIVE meta description (getpagedata.field_meta_description). Type
            // in the Inspector to override per page. Formatting/layout unchanged.
            { bind: 'field_meta_description', size: 18, color: TEXT, lineHeight: 1.6, marginBottom: 22 }),
          ROW({ gap: 16, align: 'center' }, [
            COL({ weight: 1 }, [CTA('apply',  { align: 'center', fullWidth: true })]),
            COL({ weight: 1 }, [CTA('enroll', { align: 'center', fullWidth: true })]),
            COL({ weight: 2 }, []),
          ]),
        ]),
        // Right: featured image card, fully structured from native nodes so
        // every part is click-to-edit in the editor:
        //   • navy Card container (#003C7D), rounded + padded
        //   • native Image node  — click to upload/replace the photo
        //   • Text node (name)   — gold bold
        //   • Text node (role)   — small white
        // No HTML block, no overlay: the image sits at the top of the card and
        // the two lines sit below it on the navy, matching the reference card.
        COL({ weight: 28, alignItems: 'center' }, [
          // Navy card hugs a ~260px image (card 284 − 24 side padding = 260),
          // centered in the column. On mobile the template CSS relaxes
          // .n-card to width:auto and caps images at 100%, so it stays fluid.
          CARD({
            background: NAVY, border: false, shadow: true,
            radius: 8, width: '284px', padding: { t: 12, r: 12, b: 14, l: 12 },
          }, [
            IMG({ src: HERO_PHOTO, alt: 'Course mentor', width: '260px', radius: 6 }),
            TX('DAN EVANS', {
              size: 15, weight: '700', color: '#F3AC3A',
              align: 'left', marginTop: 12, marginBottom: 2,
            }),
            TX('Former GM, Los Angeles Dodgers', {
              size: 12, color: WHITE, lineHeight: 1.4, marginBottom: 0,
            }),
          ]),
        ]),
      ]),
    ]
  );

  // -- 2. Paragraph --------------------------------------------------
  const paragraph = SEC({ padding: { t: 6, r: 20, b: 22, l: 20 } }, [
    TX('Dreaming of a career in Major League Baseball? Whether your goal is to become a scout, front office ' +
       'executive, or General Manager, this 8-week online course gives you direct access to the strategies used ' +
       'by MLB decision-makers. Learn from former Los Angeles Dodgers GM Dan Evans and veteran MLB scout Mark ' +
       'Jones as they teach you how to evaluate players, write professional scouting reports, and understand ' +
       'the inner workings of baseball operations.',
      { size: 13.5, marginBottom: 12 }),
    TX('For years more baseball fans across the U.S. and beyond, this program has launched hundreds of careers. ' +
       'With alumni now working for the Yankees, Dodgers, Red Sox, Cubs, Braves, and more, you are joining a ' +
       'proven pipeline into professional baseball. This is your first step toward baseball jobs in scouting, ' +
       'analytics, player development, and front office roles.',
      { size: 13.5, marginBottom: 0 }),
  ]);

  // -- 3. Chat box — WIRED -------------------------------------------
  const chatBox = SEC({ padding: { t: 6, r: 20, b: 24, l: 20 } }, [
    // Live, read-only: the start date and every mentor schedule load from the
    // course record via getpagedata. Only the source binding is stored.
    DYN('next-start-date', pathSeed),
  ]);

  // -- 4. Mentors — WIRED --------------------------------------------
  // The mentor carousel renders its own heading ("Your Mentors Work in the
  // Game") + sub-line above the cards, so every page — including ones created
  // before that was added — shows them. Nothing extra is placed here (adding
  // a heading node too would duplicate it).
  const mentors = SEC({}, [DYN('dynamic-mentors', nidSeed)]);

  // -- 5. What You'll Learn ------------------------------------------
  // Shared H2 (from Global Course Content, not a prop) + a per-course body.
  // The one-per-line bullets come from the shared WYL default
  // (whatYouLearnDefaults.js) — the SAME constant the palette-drop registry
  // default uses — so a manually-added What You'll Learn starts with the same
  // original body as this template one. The heading is global.
  const whatYouLearn = SEC({}, [
    N('dynamic-what-you-learn', {
      bind: '',
      bullets: WYL_DEFAULT_BULLETS,
    }),
  ]);

  // -- 6. Alumni — WIRED ---------------------------------------------
  const alumni = SEC({}, [DYN('dynamic-success-stories', alumniSeed)]);

  // -- 7. Jobs & Salary ----------------------------------------------
  const jobsSalary = SEC({}, [
    H('Jobs This Prepares You For & Pay'),
    payRow('Area / amateur scout', '$35K - $55K', 'Baseball ops analyst', '$60K - $90K'),
    SP(8),
    payRow('Pro scout', '$50K - $80K', 'Player development', '$45K - $75K'),
    SP(8),
    payRow('Crosschecker', '$70K - $110K', 'Assistant GM', '$150K - $300K'),
    SP(8),
    payRow('Scouting coordinator / director', '$85K - $150K', 'General manager', '$500K - $2M+'),
  ]);

  // -- 8. CTA — WIRED ------------------------------------------------
  const cta = SEC(
    { padding: { t: 18, r: 20, b: 22, l: 20 }, textAlign: 'center' },
    [
      ROW({ gap: 14, align: 'center', justify: 'center' }, [
        COL({ weight: 1 }, [CTA('apply',  { align: 'center', fullWidth: true })]),
        COL({ weight: 1 }, [CTA('enroll', { align: 'center', fullWidth: true })]),
      ]),
    ]
  );

  // -- 9. Course body ------------------------------------------------
  const courseBody = SEC({}, [
    H('Course Format', { size: 18, marginBottom: 10 }),
    LIST([
      ['Length:', '8 weeks, 100% online'],
      ['Live Weekly Zoom Chats:', 'Interactive sessions with Dan Evans, Mark Jones, and MLB guests'],
      ['Assignments:', 'Write scouting reports, evaluate player data, and practice front office decision-making'],
      ['Networking:', "Access SMWW's global alumni network of 30,000+ professionals"],
      ['Class Size:', 'Limited to 25 students for direct mentorship'],
    ]),
    SP(20),

    H('Who Should Take This Course?', { size: 18, marginBottom: 10 }),
    LIST([
      'Baseball fans who want to break into scouting or front office roles',
      'Former players or coaches transitioning into MLB operations',
      'Students and graduates pursuing sports management careers',
      'Anyone passionate about baseball who wants insider training and connections to MLB hiring pipelines',
    ]),
    SP(20),

    H('What Happens After I Complete This Course?', { size: 18, marginBottom: 10 }),

    H('Join the SMWW Scouting Network', { level: 'h3', size: 15, marginBottom: 8 }),
    LIST([
      "As a graduate of the Baseball GM & Scouting Course, you'll be invited to join SMWWScout.com — our exclusive network of certified scouts working across pro and amateur baseball.",
      'Create your own profile page with your bio, headshot, and scouting report samples.',
      'Advertise yourself as a trusted evaluator to scouting agents, teams, and talent identification.',
      'Think of it as your ticket makers — connecting you to the people and organizations who need your skills.',
    ]),
    SP(16),

    H('Take the Next Step in Your Baseball Career', { level: 'h3', size: 15, marginBottom: 8 }),
    LIST([
      "Your pathway into professional baseball doesn't end with graduation — it begins there. As part of the SMWW community, you'll:",
      'Build a custom baseball portfolio with your scouting work.',
      'Leverage our extensive network of MLB and MiLB alumni.',
      'Access career opportunities where SMWW alumni are already working across nearly every Major League Baseball team.',
      'Our goal is simple: to see you working inside the game you love.',
    ]),
    SP(16),

    H('Resume Package Service', { level: 'h3', size: 15, marginBottom: 8 }),
    LIST([
      'Getting the right skills is only half the battle — marketing yourself is the other. That is why SMWW provides a resume redesign service tailored for the sports industry:',
      'Highlight your new skills in baseball operations and scouting.',
      'Add credibility with a professional reference from Dan Evans and Mark Jones.',
      'Optimize your resume to get noticed by hiring managers over resumes from any industry.',
      "We'll help you present yourself as a serious candidate for front office, scouting, and baseball operations jobs.",
    ]),
    SP(16),

    H('Course Certificate & Resume Reference', { level: 'h3', size: 15, marginBottom: 8 }),
    LIST([
      'Graduates receive an official SMWW Certificate of Completion, plus a credential recognized across the sports industry. This certificate demonstrates:',
      'Your mastery of baseball scouting and GM principles.',
      'Your commitment to professional development.',
      'Your ability to compete for higher positions within the business of baseball.',
    ]),
    SP(16),

    H('Learn the Tools the Pros Use', { level: 'h3', size: 15, marginBottom: 8 }),
    LIST([
      "Today's scouts and front offices rely heavily on technology and data analytics. In this course, you'll gain exposure to the same tools MLB teams are using, including:",
      'Pitch-by-pitch data analysis.',
      'Player speed and performance tracking.',
      'Real-time video analysis tools.',
      'Organizations like the Yankees, Cardinals, and White Sox are already leading the way in this digital transformation — and you will be trained in the same cutting-edge techniques.',
    ]),
    SP(22),

    ROW({ gap: 14, align: 'center', justify: 'center' }, [
      COL({ weight: 1 }, [CTA('apply',  { align: 'center', fullWidth: true })]),
      COL({ weight: 1 }, [CTA('enroll', { align: 'center', fullWidth: true })]),
    ]),
  ]);

  // -- 10. Course Details --------------------------------------------
  const courseDetails = SEC({}, [
    CARD({ padding: { t: 0, r: 0, b: 0, l: 0 } }, [
      COL({ weight: 1, background: HEAD_BG, padding: { t: 12, r: 16, b: 12, l: 16 } }, [
        H('COURSE DETAILS', { align: 'center', size: 16, letterSpacing: 1.4, marginBottom: 0 }),
      ]),
      COL({ weight: 1, padding: { t: 18, r: 20, b: 18, l: 20 } }, [
        detailRow('Weekly Interactive Zoom Video Chats:',
          'A unique aspect of each 8-week course is the opportunity for students to participate in weekly live ' +
          'interactive Zoom video chats with fellow students, Amanda Fuller, Dr. Lashbrook, and occasional guests. ' +
          'Get to know your Mentors and fellow students personally.', ICON_ZOOM),
        detailRow('Online Education:',
          'You will enjoy the online discussion boards, weekly readings, weekly quizzes, and course projects. ' +
          'All course materials and textbooks are included in the course fee.', ICON_ONLINE),
        detailRow('Global Participation:',
          'SMWW students participate from over 149 countries. Courses have an international influence and ' +
          'instructors will cater assignments to specific countries or leagues.', ICON_GLOBE),
        detailRow('University Credit Available:',
          'For additional fees to Fort Hays State University in Kansas, you may receive graduate or undergraduate ' +
          'university credit for Sports Management Worldwide courses.', ICON_CREDIT),
        detailRow('Optional NFL Combine &amp; Senior Bowl Events:',
          'SMWW students and faculty are invited to the NFL Combine Career Conference in Indianapolis and the ' +
          'Senior Bowl in Mobile, Alabama. These events offer unmatched access to NFL executives, scouts, and ' +
          'agents. Students and Alumni are encouraged to attend to apply their skills, grow their network, and ' +
          'meet their mentors in person. All SMWW students and alumni always enjoy special pricing.', ICON_EVENT),
        detailRow('Football Front Office Advanced Certificate:',
          'Our Advanced Certificates have been hand-selected by our team of sports industry experts to put you on ' +
          'a path to success. These bundles of courses allow you to take advantage of our biggest discount and ' +
          'ensure you become an expert in that field.', ICON_CERT),
        detailRow('Military Discount:',
          'For those individuals who represent their security in military service (active, reserve, or retired) ' +
          'we offer a discount. Thank you for serving your country!', ICON_SHIELD),
        detailRow('Tax Write Off - (For USA Citizens):',
          'SMWW course tuition, books, and supplies can be used for education tax credits. Save your receipts ' +
          'and consult with your tax advisor on how this applies to you.', ICON_TAX),
      ]),
    ]),
  ]);

  // -- 11. Payment ---------------------------------------------------
  const payment = SEC({}, [
    H('Four Ways to Pay', { marginBottom: 6 }),
    TX('Same course, same access — choose the plan that fits. Every plan includes all 8 live weeks, ' +
       'mentor feedback, and lifetime alumni network access.',
      { size: 13, color: MUTED, marginBottom: 18 }),
    ROW({ gap: 16, align: 'stretch' }, [
      planCard('Pay in full',        '$1,950',   '', sourcePath, productId),
      planCard('Pay in 2',           '2 = $975', '', sourcePath, productId),
      planCard('Pay in 3',           '3 = $650', '', sourcePath, productId),
      planCard('Buy now, pay later', '4 = $488', '', sourcePath, productId),
    ]),
    SP(14),
    ROW({ gap: 12, align: 'center' }, [
      COL({ weight: 1 }, [CTA('enroll', { align: 'left' })]),
      COL({ weight: 3 }, [
        TX('MORE PAYMENT DETAILS »', { size: 12, color: BLUE, weight: '700', marginBottom: 0 }),
      ]),
    ]),
  ]);

  // -- 12. Quick Form — WIRED ----------------------------------------
  const quickForm = SEC(
    { background: NAVY, padding: { t: 30, r: 20, b: 34, l: 20 }, maxWidth: 780 },
    [DYN('dynamic-quick-form', pathSeed)]
  );

  // -- 13. Related ---------------------------------------------------
  const related = SEC({ padding: { t: 30, r: 20, b: 40, l: 20 } }, [
    H('You Might Also Like'),

    bandLabel('RELATED COURSES'),
    ROW({ gap: 20, align: 'stretch' }, [
      relCard('Athlete Management — Become a Sports Agent', 'Mentor: Dr. Lashbrook',
        'Represent professional athletes and learn the business of athlete representation.'),
      relCard('Athlete Marketing and Branding', 'Mentor: Amy Scott',
        'Build athlete brands, land endorsements, and manage public image.'),
      relCard('Baseball Agent', 'Mentor: John Boggs',
        'Learn how to become a certified MLBPA agent and represent baseball players.'),
    ]),
    SP(24),

    bandLabel('UPCOMING CONFERENCES & EVENTS'),
    ROW({ gap: 20, align: 'stretch' }, [
      relCard('Nashville Meet & Greet', 'Nashville, Tennessee',
        'Network with SMWW mentors, alumni, and sports industry professionals.'),
      relCard('NFL Australia Meet and Greet', 'Melbourne, Australia',
        'Connect with the growing NFL community across Australia and the Pacific.'),
      relCard('Baseball Career Conference', 'Orlando, Florida',
        'Meet scouts, executives, and agents working across professional baseball.'),
    ]),
    SP(24),

    bandLabel('DEGREE PROGRAMS'),
    ROW({ gap: 20, align: 'stretch' }, [
      relCard('Masters in Sports Management', 'Fort Hays State University',
        'Earn graduate credit toward a sports management degree.'),
      relCard('Masters of Sports Leadership', 'Concordia University Chicago',
        'Advance into leadership roles across the sports industry.'),
      relCard('MSM in Sports Management', 'Concordia University Chicago',
        'A graduate pathway built for working sports professionals.'),
    ]),
  ]);

  return {
    id: uid(),
    type: 'root',
    props: rootProps,
    children: [
      HTML(TEMPLATE_CSS),
      hero,
      paragraph,
      chatBox,
      mentors,
      whatYouLearn,
      alumni,
      jobsSalary,
      cta,
      courseBody,
      courseDetails,
      payment,
      quickForm,
      related,
    ],
  };
}

export default makeCourseTemplate;
